'use client'

import { useState, useCallback } from 'react'
import { useAuth } from './useAuth'
import {
  getGuestSearchStatus,
  incrementGuestSearchCount,
  GUEST_DAILY_SEARCH_LIMIT,
  GUEST_SEARCH_WARNING_THRESHOLD
} from '@/lib/search-limits'

export interface GuestSearchLimitState {
  /** Whether the current user is a guest (not authenticated) */
  isGuest: boolean
  /** Number of searches remaining today */
  remaining: number
  /** Whether the limit has been reached */
  isLimited: boolean
  /** Total number of searches used today */
  count: number
  /** The daily limit */
  limit: number
  /** Whether to show a warning (remaining < threshold) */
  showWarning: boolean
  /** Whether offline mode is active (unlimited searches) */
  isOfflineUnlimited: boolean
  /** Increment the search count - call this when a search is performed */
  incrementCount: () => void
  /** Refresh the status (useful after login/logout) */
  refresh: () => void
}

// For SSR compatibility
const getServerSnapshot = () => ({
  remaining: GUEST_DAILY_SEARCH_LIMIT,
  isLimited: false,
  count: 0
})

/**
 * Hook to manage guest search rate limiting
 *
 * Returns unlimited state for:
 * - Authenticated users
 * - Users with offline mode enabled (using local SQLite database)
 *
 * For guests without offline mode, tracks and limits daily searches.
 */
export function useGuestSearchLimit(
  isOfflineMode?: boolean
): GuestSearchLimitState {
  const { user } = useAuth()

  // State version to trigger re-renders when status changes
  const [version, setVersion] = useState(0)

  // Get status directly (this is a synchronous read from localStorage)
  // The version variable is used to trigger re-renders
  const status =
    typeof window !== 'undefined'
      ? version >= 0
        ? getGuestSearchStatus()
        : getGuestSearchStatus()
      : getServerSnapshot()

  const incrementCount = useCallback(() => {
    // Don't increment count if offline mode (no server cost)
    if (!user && !isOfflineMode) {
      incrementGuestSearchCount()
      // Trigger re-render to get fresh status
      setVersion((v) => v + 1)
    }
  }, [user, isOfflineMode])

  const refresh = useCallback(() => {
    setVersion((v) => v + 1)
  }, [])

  // Authenticated users have unlimited access
  if (user) {
    return {
      isGuest: false,
      remaining: Infinity,
      isLimited: false,
      count: 0,
      limit: Infinity,
      showWarning: false,
      isOfflineUnlimited: false,
      incrementCount: () => {}, // No-op for authenticated users
      refresh
    }
  }

  // Offline mode users have unlimited access (no server cost)
  if (isOfflineMode) {
    return {
      isGuest: true,
      remaining: Infinity,
      isLimited: false,
      count: 0,
      limit: Infinity,
      showWarning: false,
      isOfflineUnlimited: true,
      incrementCount: () => {}, // No-op for offline mode
      refresh
    }
  }

  return {
    isGuest: true,
    remaining: status.remaining,
    isLimited: status.isLimited,
    count: status.count,
    limit: GUEST_DAILY_SEARCH_LIMIT,
    showWarning:
      status.remaining > 0 &&
      status.remaining <= GUEST_SEARCH_WARNING_THRESHOLD,
    isOfflineUnlimited: false,
    incrementCount,
    refresh
  }
}
